/**
 * Organizations feature — TanStack Query hooks (CIAM Slice 10).
 *
 * The single network boundary for the feature. Conventions follow
 * features/tenants/queries.ts (the golden path): every mutation is
 * wrapped in withStepUp — the archetype PUT is the one the server
 * actually step-up-gates (Slice 5 review F1), and the wrapper is
 * transparent everywhere else — and onSuccess surgically updates the
 * detail key then broad-invalidates the list prefix.
 *
 * Capacity note (Slice 7): live_user_count arrives ONLY on the read
 * projections; mutation responses omit it. Every onSuccess therefore
 * INVALIDATES the detail key instead of setQueryData-ing the mutation
 * result — writing a count-less record into the cache would render
 * "— of N seats" until the next visit.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  organizationSchema,
  organizationsListResponseSchema,
  type CreateOrganizationInput,
  type OrgArchetype,
  type Organization,
  type OrganizationsListResponse,
  type OrganizationsListSearch,
  type UpdateOrganizationInput,
  orgGrantListSchema,
  orgGrantSchema,
  type CreateOrgGrantInput,
  type OrgGrant,
} from "./schemas";

function orgBase(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/organizations`;
}

// ── Fetchers (exported so route loaders can prefetch) ────────────────────

export async function fetchOrganizationsList(
  tenantID: string,
  search: OrganizationsListSearch,
): Promise<OrganizationsListResponse> {
  const raw = await apiFetch<unknown>(orgBase(tenantID), {
    searchParams: {
      status: search.status,
      cursor: search.cursor,
    },
  });
  return organizationsListResponseSchema.parse(raw);
}

export async function fetchOrganization(
  tenantID: string,
  orgID: string,
): Promise<Organization> {
  const raw = await apiFetch<unknown>(
    `${orgBase(tenantID)}/${encodeURIComponent(orgID)}`,
  );
  return organizationSchema.parse(raw);
}

/** Bounded picker results; the cursor tells the combobox to ask for a narrower search. */
export async function searchOrganizations(tenantID: string, query: string, signal?: AbortSignal) {
  const raw = await apiFetch<unknown>(orgBase(tenantID), {
    searchParams: { q: query, scim_eligible: true, limit: 20, include_usage: false },
    signal,
  });
  const result = organizationsListResponseSchema.parse(raw);
  return { items: result.items, hasMore: Boolean(result.next_cursor) };
}

/** Resolve only rendered grant rows, without counting their members. */
export function useOrganizationSummaryQuery(tenantID: string, orgID: string) {
  return useQuery<Organization, ApiError>({
    queryKey: [...queryKeys.organizations.detail(tenantID, orgID), "summary"],
    queryFn: async () => organizationSchema.parse(await apiFetch<unknown>(
      `${orgBase(tenantID)}/${encodeURIComponent(orgID)}`,
      { searchParams: { include_usage: false } },
    )),
    enabled: tenantID.length > 0 && orgID.length > 0,
    staleTime: 30_000,
  });
}

/**
 * Individual-customer tenants have one internal identity partition. This
 * lookup asks only for the first two active rows (two is enough to detect a
 * broken invariant) and explicitly skips live-user counts and directory
 * roll-ups, so opening Authentication does not scan a million-user population.
 */
export function useConsumerPartitionQuery(tenantID: string) {
  return useQuery<OrganizationsListResponse, ApiError>({
    queryKey: queryKeys.organizations.list(tenantID, { consumerPartition: true }),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(orgBase(tenantID), {
        searchParams: { status: "active", limit: 2, include_usage: false },
      });
      return organizationsListResponseSchema.parse(raw);
    },
    enabled: tenantID.length > 0,
  });
}

// ── Queries ──────────────────────────────────────────────────────────────

export function useOrganizationsQuery(
  tenantID: string,
  search: OrganizationsListSearch,
) {
  return useQuery<OrganizationsListResponse, ApiError>({
    queryKey: queryKeys.organizations.list(tenantID, search),
    queryFn: () => fetchOrganizationsList(tenantID, search),
    enabled: tenantID.length > 0,
  });
}

export function useOrganizationQuery(tenantID: string, orgID: string) {
  return useQuery<Organization, ApiError>({
    queryKey: queryKeys.organizations.detail(tenantID, orgID),
    queryFn: () => fetchOrganization(tenantID, orgID),
    enabled: tenantID.length > 0 && orgID.length > 0,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────

function invalidateOrg(
  qc: ReturnType<typeof useQueryClient>,
  tenantID: string,
  orgID?: string,
) {
  if (orgID) {
    // Invalidate, never setQueryData: see the capacity note above.
    void qc.invalidateQueries({
      queryKey: queryKeys.organizations.detail(tenantID, orgID),
    });
  }
  void qc.invalidateQueries({ queryKey: queryKeys.organizations.all(tenantID) });
}

export function useCreateOrganizationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Organization, ApiError, CreateOrganizationInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(orgBase(tenantID), {
          method: "POST",
          body: input,
        }).then((raw) => organizationSchema.parse(raw)),
      ),
    onSuccess: (created) => invalidateOrg(qc, tenantID, created.id),
  });
}

export function useUpdateOrganizationMutation(tenantID: string, orgID: string) {
  const qc = useQueryClient();
  return useMutation<Organization, ApiError, UpdateOrganizationInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${orgBase(tenantID)}/${encodeURIComponent(orgID)}`, {
          method: "PATCH",
          body: input,
        }).then((raw) => organizationSchema.parse(raw)),
      ),
    onSuccess: () => invalidateOrg(qc, tenantID, orgID),
  });
}

/**
 * The org's population archetype is a sign-in door: its mutation
 * rides the dedicated step-up-gated PUT — deliberately absent from
 * PATCH, and the server's strict decoder refuses smuggling attempts.
 */
export function useSetOrgArchetypeMutation(tenantID: string, orgID: string) {
  const qc = useQueryClient();
  return useMutation<Organization, ApiError, OrgArchetype>({
    mutationFn: (archetype) =>
      withStepUp(() =>
        apiFetch<unknown>(
          `${orgBase(tenantID)}/${encodeURIComponent(orgID)}/population-archetype`,
          { method: "PUT", body: { population_archetype: archetype } },
        ).then((raw) => organizationSchema.parse(raw)),
      ),
    onSuccess: () => invalidateOrg(qc, tenantID, orgID),
  });
}

export function useSuspendOrganizationMutation(tenantID: string, orgID: string) {
  const qc = useQueryClient();
  return useMutation<Organization, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(
          `${orgBase(tenantID)}/${encodeURIComponent(orgID)}/suspend`,
          { method: "POST" },
        ).then((raw) => organizationSchema.parse(raw)),
      ),
    onSuccess: () => invalidateOrg(qc, tenantID, orgID),
  });
}

export function useReactivateOrganizationMutation(
  tenantID: string,
  orgID: string,
) {
  const qc = useQueryClient();
  return useMutation<Organization, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(
          `${orgBase(tenantID)}/${encodeURIComponent(orgID)}/reactivate`,
          { method: "POST" },
        ).then((raw) => organizationSchema.parse(raw)),
      ),
    onSuccess: () => invalidateOrg(qc, tenantID, orgID),
  });
}

export function useDeleteOrganizationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (orgID) =>
      withStepUp(() =>
        apiFetch<void>(`${orgBase(tenantID)}/${encodeURIComponent(orgID)}`, {
          method: "DELETE",
        }),
      ),
    onSuccess: (_res, orgID) => {
      qc.removeQueries({
        queryKey: queryKeys.organizations.detail(tenantID, orgID),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.organizations.all(tenantID),
      });
    },
  });
}

// ── Delegated access (F79) ──────────────────────────────────────────────────
//
// Reads ride org_admins.read; a grant and a revoke ride org_admins.manage,
// which is step-up-required on the server, so both run the ceremony.

function adminGrantsBase(tenantID: string, adminID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/admin-users/${encodeURIComponent(adminID)}/org-grants`;
}

export async function fetchOrgGrants(tenantID: string, orgID: string, includeRevoked: boolean): Promise<OrgGrant[]> {
  const raw = await apiFetch<unknown>(`${orgBase(tenantID)}/${encodeURIComponent(orgID)}/admin-grants${includeRevoked ? "?include_revoked=true" : ""}`);
  return orgGrantListSchema.parse(raw).items;
}

export function useOrgGrantsQuery(tenantID: string, orgID: string, includeRevoked = false) {
  return useQuery<OrgGrant[], ApiError>({
    queryKey: queryKeys.organizations.grants(tenantID, orgID, includeRevoked),
    queryFn: () => fetchOrgGrants(tenantID, orgID, includeRevoked),
    enabled: tenantID.length > 0 && orgID.length > 0,
    staleTime: 30_000,
  });
}

export async function fetchTenantOrgGrants(tenantID: string, includeRevoked: boolean): Promise<OrgGrant[]> {
  const raw = await apiFetch<unknown>(`/api/v1/tenants/${encodeURIComponent(tenantID)}/org-grants${includeRevoked ? "?include_revoked=true" : ""}`);
  return orgGrantListSchema.parse(raw).items;
}

export function useTenantOrgGrantsQuery(tenantID: string, includeRevoked = false) {
  return useQuery<OrgGrant[], ApiError>({
    queryKey: queryKeys.organizations.allGrants(tenantID, includeRevoked),
    queryFn: () => fetchTenantOrgGrants(tenantID, includeRevoked),
    enabled: tenantID.length > 0,
    staleTime: 30_000,
  });
}

export function useCreateOrgGrantMutation(tenantID: string, orgID: string) {
  const qc = useQueryClient();
  return useMutation<OrgGrant, ApiError, CreateOrgGrantInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(adminGrantsBase(tenantID, input.admin_user_id), {
          method: "POST",
          body: { organization_id: orgID, role: input.role, ...(input.expires_at ? { expires_at: input.expires_at } : {}) },
        }).then((raw) => orgGrantSchema.parse(raw)),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.organizations.all(tenantID) }),
  });
}

export function useRevokeOrgGrantMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, OrgGrant>({
    mutationFn: (grant) =>
      withStepUp(() => apiFetch<void>(`${adminGrantsBase(tenantID, grant.admin_user_id)}/${encodeURIComponent(grant.id)}`, { method: "DELETE" })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.organizations.all(tenantID) }),
  });
}
